/**
 * Admin — Institutions Panel.
 * CRUD for college users who use the Institution Portal.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/config/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash, GraduationCap, PencilSimple, Copy, UserSwitch } from '@phosphor-icons/react';
import { startImpersonation } from '@/utils/impersonation';

export default function InstitutionsPanel({ colleges = [] }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', full_name: '', college_ids: [], institution_role: 'staff' });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/institutions`);
      setUsers(data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load institutions');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ email: '', password: '', full_name: '', college_ids: [], institution_role: 'staff' });
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email,
      password: '',
      full_name: u.full_name || '',
      college_ids: (u.college_ids && u.college_ids.length ? u.college_ids : (u.college_id ? [u.college_id] : [])),
      institution_role: (u.institution_role || 'staff').toLowerCase(),
    });
    setOpen(true);
  };
  const toggleCollege = (cid) => {
    setForm(f => ({
      ...f,
      college_ids: f.college_ids.includes(cid) ? f.college_ids.filter(x => x !== cid) : [...f.college_ids, cid],
    }));
  };
  const save = async () => {
    if (!form.email.trim() || form.college_ids.length === 0) { toast.error('Email and at least one college are required'); return; }
    if (!editing && !form.password.trim()) { toast.error('Password is required for new users'); return; }
    setSaving(true);
    try {
      if (editing) {
        const body = { full_name: form.full_name, college_ids: form.college_ids, institution_role: form.institution_role };
        if (form.password.trim()) body.password = form.password;
        await axios.patch(`${API}/admin/institutions/${editing.id}`, body);
        toast.success('Updated');
      } else {
        await axios.post(`${API}/admin/institutions`, { ...form });
        toast.success('Institution user created');
      }
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try {
      await axios.delete(`${API}/admin/institutions/${u.id}`);
      toast.success('Deleted');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };
  const copyLoginUrl = () => {
    const url = `${window.location.origin}/login`;
    navigator.clipboard.writeText(url);
    toast.success('Login URL copied');
  };

  // Impersonation — jump into an institution user's portal to see exactly
  // what they see. Banner + exit are handled globally.
  const impersonate = async (u) => {
    if (!window.confirm(`Log in as ${u.full_name || u.email}? You'll see their institution portal. Use the yellow banner to exit.`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/institutions/${u.id}/impersonate`);
      startImpersonation({ token: data.token, info: data.impersonating });
      toast.success(`Viewing as ${data.impersonating.name}`);
      window.location.href = data.impersonating.redirect_to || '/institution';
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Impersonation failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-institutions-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Libre Baskerville', serif" }}>Institution Portal Users</h2>
          <p className="text-sm text-gray-500 mt-1">College reps who verify docs and send Enrollment Agreements. Each user is scoped to a single college.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={copyLoginUrl} data-testid="copy-login-url-btn"><Copy size={14} className="mr-1" /> Copy login URL</Button>
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={openCreate} data-testid="new-institution-btn"><Plus size={14} className="mr-1" /> New Institution User</Button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-xs uppercase text-gray-500 tracking-wider text-left">
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Colleges managed</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={4} className="text-center py-10 text-gray-400">Loading…</td></tr>}
            {!loading && users.length === 0 && (
              <tr><td colSpan={4} className="text-center py-14 text-gray-400">
                <GraduationCap size={32} className="mx-auto mb-2 text-gray-300" />
                No institution users yet. Create one so the college can start verifying documents.
              </td></tr>
            )}
            {users.map(u => {
              const names = (u.college_names && u.college_names.length) ? u.college_names : (u.college_name ? [u.college_name] : []);
              return (
                <tr key={u.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`institution-user-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-gray-900">{u.full_name || '—'}</p>
                      {(() => {
                        const role = (u.institution_role || 'staff').toLowerCase();
                        return (
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${role === 'president' ? 'bg-purple-50 text-purple-800 border-purple-200' : 'bg-sky-50 text-sky-800 border-sky-200'}`}
                            data-testid={`institution-role-badge-${u.id}`}
                          >
                            {role}
                          </span>
                        );
                      })()}
                    </div>
                    <p className="text-xs text-gray-500">{u.email}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {names.length === 0 ? '—' : (
                      <div className="flex flex-wrap gap-1">
                        {names.map(n => (
                          <span key={n} className="inline-block px-2 py-0.5 text-[11px] rounded bg-indigo-50 text-indigo-800 border border-indigo-100">{n}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="ghost" title="Login as this user" onClick={() => impersonate(u)} data-testid={`impersonate-institution-${u.id}`}><UserSwitch size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-institution-${u.id}`}><PencilSimple size={14} /></Button>
                    <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700" onClick={() => remove(u)} data-testid={`delete-institution-${u.id}`}><Trash size={14} /></Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="institution-user-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit institution user' : 'New institution user'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} disabled={!!editing} data-testid="institution-email-input" />
            </div>
            <div>
              <Label className="text-xs">Full name</Label>
              <Input value={form.full_name} onChange={(e) => setForm(f => ({ ...f, full_name: e.target.value }))} data-testid="institution-name-input" />
            </div>
            <div>
              <Label className="text-xs">Assigned colleges (tick every college this user should manage)</Label>
              <div className="mt-1 border border-gray-200 rounded-md max-h-52 overflow-y-auto divide-y divide-gray-100" data-testid="institution-colleges-checklist">
                {colleges.length === 0 && (
                  <p className="p-3 text-sm text-gray-400">No colleges configured yet — create one under the Colleges tab first.</p>
                )}
                {colleges.map(c => {
                  const on = form.college_ids.includes(c.id);
                  return (
                    <label key={c.id} className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${on ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}>
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggleCollege(c.id)}
                        data-testid={`institution-college-toggle-${c.id}`}
                      />
                      <span className="flex-1">{c.name}</span>
                      {on && <span className="text-[10px] text-indigo-600 uppercase font-medium">managing</span>}
                    </label>
                  );
                })}
              </div>
              {form.college_ids.length > 0 && (
                <p className="text-[11px] text-gray-500 mt-1">{form.college_ids.length} college{form.college_ids.length > 1 ? 's' : ''} selected</p>
              )}
            </div>
            <div>
              <Label className="text-xs">{editing ? 'New password (leave blank to keep unchanged)' : 'Password'}</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} placeholder={editing ? '••••••••' : ''} data-testid="institution-password-input" />
            </div>
            <div>
              <Label className="text-xs">College Role</Label>
              <div className="mt-1 grid grid-cols-2 gap-2" data-testid="institution-role-picker">
                {[
                  { key: 'staff', label: 'Staff', hint: 'Docs → EA' },
                  { key: 'president', label: 'President', hint: 'COE → 1/2 Money' },
                ].map(r => {
                  const active = form.institution_role === r.key;
                  return (
                    <button
                      key={r.key}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, institution_role: r.key }))}
                      className={`text-left rounded-lg border px-3 py-2 transition-colors ${active ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-200' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}
                      data-testid={`institution-role-option-${r.key}`}
                    >
                      <p className={`text-sm font-semibold ${active ? 'text-indigo-800' : 'text-gray-800'}`}>{r.label}</p>
                      <p className={`text-[11px] mt-0.5 ${active ? 'text-indigo-600' : 'text-gray-500'}`}>{r.hint}</p>
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-gray-500 mt-1">
                Staff sees every new student and drives docs → EA stages. President only sees students after EA Signed and drives COE Sent → 1/2 Money Received.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="institution-save-btn">
              {saving ? 'Saving…' : (editing ? 'Save changes' : 'Create user')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
